import { useEffect, useState } from 'react'
import { Link } from 'react-router'
import { useTranslation } from 'react-i18next'
import { useActivityLog } from '@/hooks/useActivityLog.ts'
import { cn } from '@/utils/cn.ts'

const PAGE_SIZE = 20

function formatActionFallback(action: string): string {
  return action.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
}

function formatDate(iso: string): string {
  const d = new Date(iso)
  return d.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

export function ActivityLogPage() {
  const { t } = useTranslation()
  const { entries, loading, hasMore, fetchLogs } = useActivityLog()
  const [offset, setOffset] = useState(0)

  const actionLabel = (action: string): string => {
    const key = `activity.action_${action}` as const
    const translated = t(key)
    return translated !== key ? translated : formatActionFallback(action)
  }

  useEffect(() => {
    fetchLogs(PAGE_SIZE, 0)
  }, [fetchLogs])

  const handleLoadMore = () => {
    const next = offset + PAGE_SIZE
    setOffset(next)
    fetchLogs(PAGE_SIZE, next)
  }

  const tableCell =
    'border-b border-line-subtle px-4 py-4 text-sm text-fg-secondary'

  return (
    <div className="max-w-content mx-auto px-4 py-8 md:px-8 md:py-12">
      <h1 className="text-fg mb-1 text-[1.75rem] font-bold max-md:text-[1.375rem]">
        {t('activity.title')}
      </h1>
      <p className="text-fg-secondary mb-12">{t('activity.subtitle')}</p>

      {entries.length === 0 && !loading ? (
        <div className="border-line text-fg-muted flex min-h-[200px] items-center justify-center rounded-xl border border-dashed text-[0.9375rem]">
          {t('activity.noActivity')}
        </div>
      ) : (
        <>
          <table className="w-full border-collapse max-md:hidden">
            <thead>
              <tr>
                <th className="border-line text-fg-muted border-b px-4 py-4 text-left text-xs font-semibold tracking-wide uppercase">
                  {t('activity.date')}
                </th>
                <th className="border-line text-fg-muted border-b px-4 py-4 text-left text-xs font-semibold tracking-wide uppercase">
                  {t('activity.action')}
                </th>
                <th className="border-line text-fg-muted border-b px-4 py-4 text-left text-xs font-semibold tracking-wide uppercase">
                  {t('activity.vault')}
                </th>
                <th className="border-line text-fg-muted border-b px-4 py-4 text-left text-xs font-semibold tracking-wide uppercase">
                  {t('activity.details')}
                </th>
              </tr>
            </thead>
            <tbody>
              {entries.map((entry) => (
                <tr key={entry.id}>
                  <td
                    className={cn(
                      tableCell,
                      'text-fg-muted text-[0.8125rem] whitespace-nowrap',
                    )}
                  >
                    {formatDate(entry.created_at)}
                  </td>
                  <td className={tableCell}>
                    <span className="bg-surface-muted text-fg inline-block rounded-sm px-2 py-0.5 text-xs font-medium">
                      {actionLabel(entry.action)}
                    </span>
                  </td>
                  <td className={tableCell}>
                    {entry.vault_id != null ? (
                      <Link
                        to={`/dashboard/vault/${entry.vault_id}`}
                        className="text-accent font-mono text-[0.8125rem] no-underline hover:underline"
                      >
                        #{entry.vault_id}
                      </Link>
                    ) : (
                      '—'
                    )}
                  </td>
                  <td className={tableCell}>
                    {Object.keys(entry.details).length > 0
                      ? JSON.stringify(entry.details)
                      : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          <div className="flex flex-col gap-4 md:hidden">
            {entries.map((entry) => (
              <div
                key={entry.id}
                className="border-line bg-surface-card flex flex-col gap-3 rounded-lg border p-4"
              >
                <div className="mb-1 flex items-center justify-between">
                  <span className="bg-surface-muted text-fg inline-block rounded-sm px-2 py-0.5 text-xs font-medium">
                    {actionLabel(entry.action)}
                  </span>
                  <span className="text-fg-muted text-[0.8125rem] whitespace-nowrap">
                    {formatDate(entry.created_at)}
                  </span>
                </div>
                {entry.vault_id != null && (
                  <div className="flex items-center justify-between">
                    <span className="text-fg-muted text-xs">
                      {t('activity.vault')}
                    </span>
                    <Link
                      to={`/dashboard/vault/${entry.vault_id}`}
                      className="text-accent font-mono text-[0.8125rem] no-underline hover:underline"
                    >
                      #{entry.vault_id}
                    </Link>
                  </div>
                )}
              </div>
            ))}
          </div>

          {hasMore && (
            <button
              type="button"
              className="border-line bg-surface-muted text-fg-secondary duration-fast hover:border-accent hover:text-accent mx-auto mt-8 block cursor-pointer rounded-md border px-6 py-2.5 text-sm font-medium transition-all disabled:opacity-50"
              onClick={handleLoadMore}
              disabled={loading}
            >
              {loading ? t('common.loading') : t('activity.loadMore')}
            </button>
          )}
        </>
      )}
    </div>
  )
}
